import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Loading, ErrorMessage, StatusMessage } from '@/src/ui';
import type { QuizListItemDTO } from '../models/quizDTOs';

interface PublishQuizModalProps {
    quiz: QuizListItemDTO;
    handleClose: (shouldRefresh: boolean, isInTimeout: boolean) => void;
}

const PublishQuizModal: React.FC<PublishQuizModalProps> = ({ quiz, handleClose }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();
        if (submitting || success) return;

        setSubmitting(true);
        setError('');
        try {
            await apiRequest('PATCH', `assessments/exams/publish/${quiz.id}`, {
                isPublished: !quiz.isPublished,
            });
            setTimeout(() => {
                handleClose(true, false);
                setSuccess(false);
            }, 750);
            setSuccess(true);
        } catch {
            setError(`Failed to ${quiz.isPublished ? 'unpublish' : 'publish'} quiz`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={true} onClose={() => handleClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>{quiz.isPublished ? 'Unpublish' : 'Publish'} Quiz</h2>
                <p>
                    Are you sure you want to {quiz.isPublished ? 'unpublish' : 'publish'} this quiz?
                </p>
                <p>
                    <strong>Name: {quiz.title}</strong>
                </p>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button variant="outlined" onClick={() => handleClose(false, false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={quiz.isPublished ? 'error' : 'success'}
                        onClick={handleSubmit}
                    >
                        {quiz.isPublished ? 'Unpublish' : 'Publish'}
                    </Button>
                </Box>
                <ErrorMessage error={error} style={{ marginTop: 8 }} />
                <StatusMessage
                    visible={success}
                    status="Quiz updated successfully!"
                    style={{ marginTop: 8 }}
                />
                <Loading visible={submitting} message="Processing..." size="sm" />
            </Box>
        </Modal>
    );
};

export default PublishQuizModal;
